import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { AsyncPipe } from '@angular/common';
import { Component, inject, type OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, NEVER, type Observable, switchMap, tap } from 'rxjs';
import { LAST_TWELVE_MONTHS_PERIOD } from '../../../constants/period.constant';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { ScaleCode, type ScaleRange, type WidgetScaleConfig } from '../../../providers/scales/scales.interface';
import { ScalesProvider } from '../../../providers/scales/scales.provider';
import { OldDataSourceCode } from '../../../services/developer-helper.service';
import { DeveloperHelperComponent } from '../../developer-helper/developer-helper';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { ScaleComponent } from '../../scale/scale.component';
import { AbstractWidgetClass } from '../AbstractWidget.class';

@UntilDestroy()
@Component({
    styleUrls: ['./widget-energy-efficiency-rating.scss'],
    selector: 'widget-energy-efficiency-rating',
    templateUrl: 'widget-energy-efficiency-rating.html',
    imports: [
        UiSectionComponent,
        UiSectionContentComponent,
        DeveloperHelperComponent,
        LoadingAndErrorComponent,
        TranslatePipe,
        ScaleComponent,
        AsyncPipe
    ]
})
export class WidgetEnergyEfficiencyRatingComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _dashboardingProvider = inject(DashboardingProvider);
    private _scalesProvider = inject(ScalesProvider);

    ranges$!: Observable<ScaleRange[]>;
    value$!: Observable<number>;

    labels!: WidgetScaleConfig;

    isLoading: boolean = false;
    hasError: boolean = false;
    hasNoData: boolean = false;

    OldDataSourceCode = OldDataSourceCode;
    lastTwelveMonths = LAST_TWELVE_MONTHS_PERIOD;

    private _scaleCode = ScaleCode.DPE;

    ngOnInit() {
        this.labels = this._scalesProvider.getScaleLabels(this._scaleCode);

        this.ranges$ = this._dashboardingProvider.getSelectedNodeId$().pipe(
            tap(() => (this.isLoading = true)),
            switchMap(classificationNodeId => this._scalesProvider.getScaleRanges$(this._scaleCode, classificationNodeId)),
            tap(ranges => {
                if (!ranges.length) {
                    this.hasNoData = true;
                }
            }),
            tap(() => (this.isLoading = false)),
            catchError(() => {
                this.hasError = true;
                this.isLoading = false;
                return NEVER;
            })
        );

        this.value$ = this._dashboardingProvider.getMeaningfulNodeIdAndSelectedPeriod$().pipe(
            tap(() => (this.isLoading = true)),
            switchMap(([classificationNodeId, period]) => {
                const periodNormalize = this._dashboardingProvider.applyNormalizePeriodTo12FullMonths(period);
                return this._scalesProvider.getScaleValue$(
                    this._scaleCode,
                    periodNormalize.start,
                    periodNormalize.end,
                    classificationNodeId
                );
            }),
            tap(() => (this.isLoading = false)),
            catchError(() => {
                this.hasError = true;
                this.isLoading = false;
                return NEVER;
            })
        );
    }
}
